import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { DollarSign, Users, TrendingUp, Search, Eye, Ban, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AffiliateData {
  id: string;
  code: string;
  user_id: string;
  commission_rate: number;
  total_earnings: number;
  total_sales: number;
  is_active: boolean;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

const AdminAffiliatesList = () => {
  const { toast } = useToast();
  const [affiliates, setAffiliates] = useState<AffiliateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAffiliates = async () => {
    try {
      setIsLoading(true);
      
      // Primeiro buscar os códigos de afiliado com comissão atualizada para 15%
      const { data: affiliateCodes, error: affiliateError } = await supabase
        .from('affiliate_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (affiliateError) {
        console.error('Erro ao buscar códigos de afiliado:', affiliateError);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os códigos de afiliado.",
          variant: "destructive",
        });
        return;
      }

      // Buscar informações dos usuários separadamente
      const affiliateData: AffiliateData[] = [];
      
      for (const affiliate of affiliateCodes || []) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('email, first_name')
          .eq('id', affiliate.user_id)
          .single();

        affiliateData.push({
          ...affiliate,
          user_email: profile?.email || 'Email não encontrado',
          user_name: profile?.first_name || 'Nome não encontrado'
        });
      }

      setAffiliates(affiliateData);
    } catch (err) {
      console.error('Erro na busca de afiliados:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAffiliateStatus = async (affiliateId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('affiliate_codes')
        .update({ is_active: !currentStatus })
        .eq('id', affiliateId);

      if (error) {
        console.error('Erro ao atualizar status:', error);
        toast({
          title: "Erro",
          description: "Não foi possível atualizar o status do afiliado.",
          variant: "destructive",
        });
        return;
      }

      await fetchAffiliates();
      toast({
        title: "Sucesso",
        description: `Afiliado ${!currentStatus ? 'ativado' : 'desativado'} com sucesso.`,
      });
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    }
  };

  const updateCommissionRate = async (affiliateId: string, newRate: number) => {
    try {
      const { error } = await supabase
        .from('affiliate_codes')
        .update({ commission_rate: newRate })
        .eq('id', affiliateId);

      if (error) {
        console.error('Erro ao atualizar comissão:', error);
        toast({
          title: "Erro",
          description: "Não foi possível atualizar a taxa de comissão.",
          variant: "destructive",
        });
        return;
      }

      await fetchAffiliates();
      toast({
        title: "Sucesso",
        description: "Taxa de comissão atualizada com sucesso.",
      });
    } catch (err) {
      console.error('Erro ao atualizar comissão:', err);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const filteredAffiliates = affiliates.filter(affiliate => {
    const matchesSearch = 
      affiliate.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const totalCommissions = affiliates.reduce((sum, aff) => sum + aff.total_earnings, 0);
  const totalSales = affiliates.reduce((sum, aff) => sum + aff.total_sales, 0);
  const activeAffiliates = affiliates.filter(aff => aff.is_active).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Afiliados</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{affiliates.length}</div>
            <p className="text-xs text-muted-foreground">
              {activeAffiliates} ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSales}</div>
            <p className="text-xs text-muted-foreground">
              Vendas através de afiliados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Comissões Pagas</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">R$ {totalCommissions.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Total em comissões
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar afiliados por código, email ou nome..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Affiliates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gerenciamento de Afiliados</CardTitle>
          <CardDescription>
            Controle todos os afiliados do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Comissão (%)</TableHead>
                <TableHead>Vendas</TableHead>
                <TableHead>Ganhos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAffiliates.length > 0 ? (
                filteredAffiliates.map((affiliate) => (
                  <TableRow key={affiliate.id}>
                    <TableCell className="font-mono font-medium">
                      {affiliate.code}
                    </TableCell>
                    <TableCell>{affiliate.user_name || 'Sem nome'}</TableCell>
                    <TableCell>{affiliate.user_email}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={affiliate.commission_rate}
                        onChange={(e) => {
                          const newRate = parseFloat(e.target.value);
                          if (newRate >= 0 && newRate <= 100) {
                            updateCommissionRate(affiliate.id, newRate);
                          }
                        }}
                        className="w-20"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </TableCell>
                    <TableCell>{affiliate.total_sales}</TableCell>
                    <TableCell>R$ {affiliate.total_earnings.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={affiliate.is_active ? "default" : "secondary"}>
                        {affiliate.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleAffiliateStatus(affiliate.id, affiliate.is_active)}
                          className={affiliate.is_active ? "text-red-500 hover:text-red-600" : "text-green-500 hover:text-green-600"}
                        >
                          {affiliate.is_active ? <Ban size={16} /> : <CheckCircle size={16} />}
                        </Button>
                        <Button size="sm" variant="ghost">
                          <Eye size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                    Nenhum afiliado encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAffiliatesList;
